import { tamanhoLegivel } from "../../utils/formatStringUtils";

/**
 * Espelho das regras de `TicketAttachmentRules.cs` (backend). O servidor continua sendo a
 * autoridade; aqui só se antecipa o erro para ele aparecer antes de o envio demorar.
 * Por que 25 MB somados: o IIS do App Service recusa corpo acima de ~28,6 MB sem que o NINA veja.
 */
export const MAX_BYTES_POR_ARQUIVO = 10 * 1024 * 1024;
export const MAX_ARQUIVOS = 5;
export const MAX_BYTES_TOTAL = 25 * 1024 * 1024;

const EXTENSOES_BLOQUEADAS = new Set([
  ".exe", ".dll", ".scr", ".com", ".pif", ".cpl", ".msi", ".msp", ".jar",
  ".bat", ".cmd", ".ps1", ".psm1", ".vbs", ".vbe", ".js", ".jse", ".wsf", ".wsh",
  ".hta", ".lnk", ".reg", ".sh", ".app",
]);

/** Extensão a partir do ÚLTIMO ponto: `relatorio.pdf.exe` devolve `.exe`. */
export function extensaoDe(nome: string): string {
  const i = nome.lastIndexOf(".");
  if (i < 0 || i === nome.length - 1) return "";
  const ext = nome.slice(i).trim().toLowerCase();
  // Igual ao backend: extensão absurda (> 12) não é extensão.
  return ext.length > 12 ? "" : ext;
}

/** Devolve o motivo da recusa (com o nome do arquivo) ou `null` quando o conjunto é aceito. */
export function validarAnexos(arquivos: Pick<File, "name" | "size">[]): string | null {
  if (arquivos.length > MAX_ARQUIVOS) {
    return `Envie no máximo ${MAX_ARQUIVOS} arquivos por vez.`;
  }
  for (const a of arquivos) {
    if (a.size <= 0) return `"${a.name}" está vazio.`;
    if (a.size > MAX_BYTES_POR_ARQUIVO) {
      return `"${a.name}" passa de ${tamanhoLegivel(MAX_BYTES_POR_ARQUIVO)}.`;
    }
    if (EXTENSOES_BLOQUEADAS.has(extensaoDe(a.name))) {
      return `"${a.name}" não pode ser enviado: arquivos ${extensaoDe(a.name)} são bloqueados.`;
    }
  }
  const total = arquivos.reduce((soma, a) => soma + a.size, 0);
  if (total > MAX_BYTES_TOTAL) {
    return `Os arquivos somam mais de ${tamanhoLegivel(MAX_BYTES_TOTAL)}.`;
  }
  return null;
}

/** Junta novos arquivos aos atuais; se o resultado violar as regras, nada é adicionado. */
export function adicionarAnexos<T extends Pick<File, "name" | "size">>(
  atuais: T[],
  novos: T[]
): { arquivos: T[]; erro: string | null } {
  const juntos = [...atuais, ...novos];
  const erro = validarAnexos(juntos);
  return erro ? { arquivos: atuais, erro } : { arquivos: juntos, erro: null };
}

const dois = (n: number) => String(n).padStart(2, "0");

/** `print-AAAA-MM-DD-HHmm.png` — nome dado à imagem colada da área de transferência. */
export function nomeDoPrint(agora: Date = new Date()): string {
  return `print-${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}-${dois(
    agora.getHours()
  )}${dois(agora.getMinutes())}.png`;
}

/**
 * Só age quando a colagem não tem texto. Imagens coladas chegam como "image.png" (nome genérico). Renomeia só as imagens; colar texto ou
 * arquivo copiado do Explorer mantém o nome original.
 */
export function arquivosDaColagem(
  dados: Pick<DataTransfer, "items" | "types"> | null | undefined
): File[] {
  if (!dados?.items) return [];
  // Excel/Word colam texto E uma imagem da seleção: com texto presente, quem cola quer o texto.
  if (Array.from(dados.types ?? []).includes("text/plain")) return [];
  const itens = dados.items;
  const arquivos: File[] = [];
  for (let i = 0; i < itens.length; i++) {
    const item = itens[i];
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (!f) continue;
    arquivos.push(
      f.type.startsWith("image/") && /^image\.\w+$/i.test(f.name)
        ? new File([f], nomeDoPrint(), { type: f.type })
        : f
    );
  }
  return arquivos;
}

/** Erro do servidor: 400 traz `{message}`; cai no texto padrão quando não há. */
export function mensagemDeErro(e: any, padrao: string): string {
  const d = e?.response?.data;
  if (typeof d === "string" && d) return d;
  return d?.message ?? padrao;
}
